import { useState, useCallback } from "react";
import {
  FileWithMetadata,
  UploadProgress,
  DocumentMetadata,
  useDocumentService,
} from "../services/documentService";
import {
  validateFiles,
  validateMetadata,
  generateUUID,
} from "../services/fileValidationService";
import { useSnackbar } from "../contexts/snackbar-context";
import { useCategories } from "./useCategories";

export const useFileUpload = () => {
  const [files, setFiles] = useState<FileWithMetadata[]>([]);
  const [uploading, setUploading] = useState<boolean>(false);
  const [progress, setProgress] = useState<UploadProgress>({});
  const [isDragging, setIsDragging] = useState<boolean>(false);

  // Document metadata
  const [documentScope, setDocumentScope] = useState<"aircraft" | "assembly" | "">(
    ""
  );
  const [assemblyType, setAssemblyType] = useState<string>("");
  const [assemblyFolder, setAssemblyFolder] = useState<string>("");
  const [assemblyCategory, setAssemblyCategory] = useState<string>("");
  const [tailNumber, setTailNumber] = useState<string>("");
  const [documentCategory, setDocumentCategory] = useState<string>("");
  const [serialNumber, setSerialNumber] = useState<string>("");
  const [aircraftType, setAircraftType] = useState<string>("");
  const [documentTitle, setDocumentTitle] = useState<string>("");
  const [description, setDescription] = useState<string>("");

  const { showError, showSuccess, showWarning } = useSnackbar();
  const { uploadDocument, updateDocument } = useDocumentService();
  const { categories } = useCategories(tailNumber);

  const getErrorMessage = useCallback((error: unknown): string => {
    if (error instanceof Error && error.message.trim()) {
      return error.message;
    }

    return "Upload failed.";
  }, []);


  const buildFolderPath = useCallback((categoryCode: string): string => {
    if (categoryCode.includes("/")) {
      return categoryCode;
    }

    const path: string[] = [categoryCode];
    const findParent = (code: string): string | undefined => {
      return categories.find((cat) => cat.code === code)?.parentCode;
    };

    let current = findParent(categoryCode);
    while (current) {
      path.unshift(current);
      current = findParent(current);
    }

    return path.join('/');
  }, [categories]);

  const handleFiles = useCallback(
    (selectedFiles: FileList): void => {
      const validation = validateFiles(selectedFiles, files);

      if (validation.errors.length > 0) {
        showError(validation.errors.join(" "));
      }

      if (validation.validFiles.length > 0) {
        const newFilesWithMetadata: FileWithMetadata[] =
          validation.validFiles.map((file) => ({
            file,
            id: generateUUID(),
            uploadProgress: 0,
            status: "pending",
          }));

        setFiles((prev) => [...prev, ...newFilesWithMetadata]);

        if (validation.errors.length === 0) {
          showSuccess(
            `Successfully added ${validation.validFiles.length} file(s)`
          );
        }
      }
    },
    [files, showError, showSuccess]
  );

  const removeFile = useCallback(
    (fileId: string): void => {
      setFiles((prev) =>
        prev.filter((fileWithMeta) => fileWithMeta.id !== fileId)
      );
      setProgress((prev) => {
        const updated = { ...prev };
        const fileName = files.find((f) => f.id === fileId)?.file.name;
        if (fileName) {
          delete updated[fileName];
        }
        return updated;
      });
    },
    [files]
  );

  const clearAllFiles = useCallback((): void => {
    setFiles([]);
    setProgress({});
    setUploading(false);
  }, []);

  const uploadFiles = useCallback(async (): Promise<void> => {
    // Validate scope is selected
    if (!documentScope) {
      showError("Please select an upload type (Aircraft or Assembly)");
      return;
    }

    // Validate based on document scope
    if (documentScope === "aircraft") {
      if (!tailNumber || !documentCategory || !documentTitle) {
        showError("Aircraft, Category, and Form No. are required");
        return;
      }
    } else if (documentScope === "assembly") {
      if (!assemblyType || !assemblyFolder || !assemblyCategory || !documentTitle) {
        showError("Assembly Type, Folder, Category, and Form No. are required");
        return;
      }
    }

    const isAssemblyCustomFolder =
      documentScope === "assembly" && assemblyCategory.includes("/");

    // For assembly standard categories, backend expects selectedFolderCode to be the category code (e.g. F001),
    // not "serial/category". For custom assembly folders, use the full folder path.
    const selectedFolderCode = documentScope === "assembly"
      ? (isAssemblyCustomFolder ? assemblyCategory : assemblyCategory)
      : buildFolderPath(documentCategory);
    
    // Determine if serial number is required:
    const isSerialNumberRequired = false;

    // Validate metadata with conditional serial number requirement (for aircraft only)
    if (documentScope === "aircraft") {
      const metadataValidation = validateMetadata(
        tailNumber,
        documentCategory,
        serialNumber,
        aircraftType,
        documentTitle,
        description,
        isSerialNumberRequired
      );

      if (!metadataValidation.isValid) {
        showError(metadataValidation.errors.join(", "));
        return;
      }
    }

    
    let documentTypeCode: string;
    let folderPath: string;

    if (documentScope === "assembly") {
      documentTypeCode = isAssemblyCustomFolder ? "CUSTOM" : assemblyCategory;
      folderPath = isAssemblyCustomFolder ? assemblyCategory : assemblyFolder;
    } else {
      const isCustomFolderSelection = documentCategory.includes("/");

      // For custom folders force CUSTOM as the documentTypeCode.
      // For standard IATA folders strip SN segment if present.
      if (isCustomFolderSelection) {
        documentTypeCode = "CUSTOM";
      } else if (selectedFolderCode.includes("SN")) {
        const parts = documentCategory.split('.');
        documentTypeCode = parts.filter(part => !part.startsWith('SN')).join('.');
      } else {
        documentTypeCode = documentCategory;
      }

      folderPath = isCustomFolderSelection
        ? documentCategory.split("/").filter(Boolean).pop() ?? documentTypeCode
        : (categories.find((cat) => cat.code === documentCategory)?.name ?? documentTypeCode);
    }

    const metadata: DocumentMetadata = {
      tailNumber: documentScope === "assembly" ? "" : tailNumber,
      selectedFolderCode,
      folderPath,
      documentTypeCode,
      serialNumber: documentScope === "assembly" ? "" : serialNumber,
      aircraftType: documentScope === "assembly" ? "" : aircraftType,
      documentTitle,
      description: documentScope === "assembly" ? "" : description,
      isQuickUpload: false,
      approvalStatus: "Pending",
      source: "DMSApp",
      // Assembly-specific metadata
      ...(documentScope === "assembly" && {
        assemblyType,
        assemblyFolder,
        assemblyCategory,
      }),
    };

    setUploading(true);

    // Reset all files to uploading status
    setFiles((prev) =>
      prev.map((file) => ({
        ...file,
        status: "uploading" as const,
        error: undefined,
      }))
    );

    const uploadPromises = files.map(async (fileWithMeta) => {
      try {
        // Update progress for this file
        setProgress((prev) => ({
          ...prev,
          [fileWithMeta.file.name]: 10,
        }));

        // Upload to API using the service
        const result = await uploadDocument(fileWithMeta.file, metadata);

        if (!result) {
          throw new Error("Upload failed.");
        }

        // Update progress to complete
        setProgress((prev) => ({
          ...prev,
          [fileWithMeta.file.name]: 100,
        }));

        // Update file status to success
        setFiles((prev) =>
          prev.map((f) =>
            f.id === fileWithMeta.id
              ? { ...f, status: "success", documentId: result.documentId }
              : f
          )
        );

        return { success: true, file: fileWithMeta.file.name, result };
      } catch (error) {
        // Update file status to error
        setFiles((prev) =>
          prev.map((f) =>
            f.id === fileWithMeta.id
              ? { ...f, status: "error", error: (error as Error).message }
              : f
          )
        );

        setProgress((prev) => ({
          ...prev,
          [fileWithMeta.file.name]: 0,
        }));

        return { success: false, file: fileWithMeta.file.name, error };
      }
    });

    try {
      const results = await Promise.allSettled(uploadPromises);

      const successfulUploads = results.filter(
        (result) => result.status === "fulfilled" && result.value.success
      ).length;

      const failedUploads = results.length - successfulUploads;

      if (failedUploads === 0) {
        showSuccess(`Successfully uploaded ${successfulUploads} file(s)!`);
      } else if (successfulUploads === 0) {
        const firstFailure = results.find(
          (result) => result.status === "fulfilled" && !result.value.success
        );
        const errorMessage =
          firstFailure && firstFailure.status === "fulfilled"
            ? getErrorMessage(firstFailure.value.error)
            : "Upload failed.";

        showError(errorMessage);
      } else {
        showWarning(
          `Uploaded ${successfulUploads} file(s), ${failedUploads} failed.`
        );
      }
    } catch (error) {
      showError(`Upload failed: ${(error as Error).message}`);
    } finally {
      setUploading(false);
    }
  }, [
    files,    documentScope,
    assemblyType,
    assemblyFolder,
    assemblyCategory,
    tailNumber,
    documentCategory,
    serialNumber,
    aircraftType,
    documentTitle,
    description,
    categories,
    uploadDocument,
    showError,
    showSuccess,
    showWarning,
    buildFolderPath,
    getErrorMessage,
  ]);

  const updateFiles = useCallback(
    async (payload: {
      documentId: string;
      tailNumber: string;
      categoryCode: string;
      serialNumber: string;
      aircraftType: string;
      documentTitle: string;
      description: string;
    }) => {
      const isSerialNumberRequired = false;

    const metadataValidation = validateMetadata(
      payload.tailNumber,
      payload.categoryCode,
      payload.serialNumber,
      payload.aircraftType,
      payload.documentTitle,
      payload.description,
      isSerialNumberRequired
    );

    if (!metadataValidation.isValid) {
      showError(metadataValidation.errors.join(", "));
      return; 
    }
      try {
        await updateDocument({
          documentId: payload.documentId,
          tailNumber: payload.tailNumber,
          categoryCode: payload.categoryCode,
          serialNumber: payload.serialNumber,
          aircraftType: payload.aircraftType,
          documentTitle: payload.documentTitle,
          description: payload.description,
        });

        showSuccess("Document metadata updated successfully");
      } catch (err) {
        showError("Failed to update document");
        throw err;
      }
    },
    [updateDocument, showSuccess, showError]
  );

  // Drag and drop handlers
  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>): void => {
      e.preventDefault();
      setIsDragging(false);
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        handleFiles(e.dataTransfer.files);
      }
    },
    [handleFiles]
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>): void => {
      e.preventDefault();
      setIsDragging(true);
    },
    []
  );

  const handleDragLeave = useCallback(
    (e: React.DragEvent<HTMLDivElement>): void => {
      e.preventDefault();
      // Only set dragging to false if leaving the upload box
      if (!e.currentTarget.contains(e.relatedTarget as Node)) {
        setIsDragging(false);
      }
    },
    []
  );

  return {
    // State
    files,
    uploading,
    progress,
    isDragging,
    documentScope,
    assemblyType,
    assemblyFolder,
    assemblyCategory,
    tailNumber,
    documentCategory,
    serialNumber,
    aircraftType,
    documentTitle,
    description,

    // Actions
    handleFiles,
    removeFile,
    clearAllFiles,
    uploadFiles,
    updateFiles,
    handleDrop,
    handleDragOver,
    handleDragLeave,

    // Setters
    setDocumentScope,
    setAssemblyType,
    setAssemblyFolder,
    setAssemblyCategory,
    setTailNumber,
    setDocumentCategory,
    setSerialNumber,
    setAircraftType,
    setDocumentTitle,
    setDescription,
  };
};
